import { useEffect, useMemo, useRef } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { popOnboardingConfetti } from '@confluence/onboarding-helpers/entry-points/popOnboardingConfetti.ts';
import { HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import type {
	OnboardingActionId,
	OnboardingActionState,
	OnboardingActionStates,
	OnboardingAction,
} from './cards/HTV2CardTypes.ts';
import { ONBOARDING_ACTION_STATES, ONBOARDING_ACTION_IDS } from './cards/HTV2CardTypes.ts';
import { transformNthUserOnboardingActions } from './cards/HTVNthUserCards.tsx';

function deserializeOnboardingActionStates(
	serializedValues: string | undefined,
): OnboardingActionStates {
	try {
		const values = JSON.parse(serializedValues || '{}');
		if (typeof values !== 'object') {
			return {};
		}

		// There's no control over the shape of the object that's deserialized,
		// so we need to filter out any keys and values that aren't valid.
		const isValidState = (value: any): value is OnboardingActionState =>
			ONBOARDING_ACTION_STATES.includes(value);

		return ONBOARDING_ACTION_IDS.reduce(
			(states, id) => (isValidState(values[id]) ? { ...states, [id]: values[id] } : states),
			{} as OnboardingActionStates,
		);
	} catch (e) {
		return {};
	}
}

function serializeOnboardingActionStates(onboardingActionStates: OnboardingActionStates) {
	return JSON.stringify(onboardingActionStates);
}

export function useNthUserOnboardingActions(): {
	actions: OnboardingAction[];
	complete: (id: OnboardingActionId) => void;
	errors: ApolloErrorInterop[];
	loading: boolean;
	isFinished: boolean;
} {
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(Object.values(HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS));

	const { setOnboardingState } = useSetOnboardingState();

	const { onboardingActionStates: serializedOnboardingActionStates, hasSeenConfetti } =
		deserializeState(onboardingStateData);

	const onboardingActionStates = useMemo(() => {
		return deserializeOnboardingActionStates(serializedOnboardingActionStates);
	}, [serializedOnboardingActionStates]);

	const complete = useMemo(
		() =>
			(id: OnboardingActionId): void => {
				setOnboardingState({
					key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.onboardingActionStates,
					value: serializeOnboardingActionStates({ ...onboardingActionStates, [id]: 'completed' }),
				});
			},
		[onboardingActionStates, setOnboardingState],
	);

	const errors = [onboardingStateError].filter(Boolean) as ApolloErrorInterop[];

	const actions: OnboardingAction[] = useMemo(() => {
		return transformNthUserOnboardingActions({ onboardingActionStates });
	}, [onboardingActionStates]);

	const isFinished = actions.every((action) => action.state !== 'pending');
	const hasPoppedConfetti = useRef(false);

	useEffect(() => {
		if (onboardingStateLoading || !isFinished) {
			return;
		}

		const popConfettiIfVisible = () => {
			if (hasSeenConfetti || hasPoppedConfetti.current || document.visibilityState !== 'visible') {
				return;
			}

			hasPoppedConfetti.current = true;
			void popOnboardingConfetti();
			void setOnboardingState([
				{
					key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.hasSeenConfetti,
					value: 'true',
				},
				{
					key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.isCollapsed,
					value: 'true',
				},
			]);
		};

		// Check to see if the user is still viewing the page. The timeout is used
		// to ensure that the confetti doesn't pop if the user is navigating away.
		const timeout = setTimeout(popConfettiIfVisible, 50);

		document.addEventListener('visibilitychange', popConfettiIfVisible);

		return () => {
			// Cleanup timeout and event listener
			clearTimeout(timeout);
			document.removeEventListener('visibilitychange', popConfettiIfVisible);
		};
	}, [onboardingStateLoading, hasSeenConfetti, isFinished, setOnboardingState]);

	return {
		actions,
		complete,
		errors,
		loading: onboardingStateLoading,
		isFinished,
	};
}
